import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Collapse, Typography } from '@mui/material';
import { ChevronDown } from 'lucide-react';
import { BaseDialog } from './BaseDialog';
import { colors } from '../../../styles/theme/colors';
import { FONT_FAMILY } from '../../../styles/theme/theme';
import { useTranslation } from '../../../hooks/useTranslation';
import { useGetCorridorCountriesQuery } from '../../../../api/userApi3';
import { calculatePricingBreakdown, buildPricingFormulaText, calculatePricePerLb, calculateBasePrice, calculatePlatformFee } from '../../../utils/pricingUtils';
import enTranslations from '../../../translations/en.json';
import offerIcon from '@/assets/offer.svg';
import cornerRipple from '@/assets/corner-ripple.svg';

const inputLabelSx = {
  fontFamily: FONT_FAMILY,
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '21px',
  color: '#484A54',
  marginBottom: '6px',
};

const readOnlyBoxSx = {
  width: '100%',
  height: '44px',
  boxSizing: 'border-box' as const,
  display: 'flex',
  alignItems: 'center',
  padding: '10px 14px',
  border: '1px solid #C6C8D0',
  borderRadius: '8px',
  boxShadow: '0px 1px 2px rgba(10, 13, 18, 0.05)',
  backgroundColor: '#F5F5F7',
};

const readOnlyTextSx: React.CSSProperties = {
  fontFamily: 'Poppins, sans-serif',
  fontWeight: 400,
  fontSize: '14px',
  color: '#181D27',
};

interface TravelerPricingDialogProps {
  open: boolean;
  onClose: () => void;
  originCode?: string;
  destinationCode?: string;
  senderWeight?: number;
  travelerPrice?: number;
  travelerWeight?: number;
  feePercentage?: number;
  isFromNotification?: boolean;
}

export const TravelerPricingDialog: React.FC<TravelerPricingDialogProps> = ({
  open,
  onClose,
  originCode,
  destinationCode,
  senderWeight,
  travelerPrice,
  travelerWeight,
  feePercentage,
  isFromNotification = false,
}) => {
  const { t } = useTranslation();
  const d = (key: string) => t(`travelerPricingDialog.${key}`);
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);
  const navigate = useNavigate();

  const handleSeeHowItWorksNow = () => {
    onClose();
    navigate('/update-profile?tab=how-pricing-works');
  };

  const { data: countries = [] } = useGetCorridorCountriesQuery();
  const originName = countries.find((c) => c.code === originCode)?.name || originCode || '--';
  const destinationName = countries.find((c) => c.code === destinationCode)?.name || destinationCode || '--';

  const hasFeePercentage = !!feePercentage;
  const hasCalculation = hasFeePercentage && !!senderWeight && !!travelerPrice && !!travelerWeight;
  const { finalAmount } = hasCalculation
    ? calculatePricingBreakdown(senderWeight!, travelerPrice!, travelerWeight!, feePercentage || 0, 'traveler')
    : { finalAmount: 0 };
  const notificationBasePrice = hasCalculation
    ? calculateBasePrice(senderWeight!, calculatePricePerLb(travelerPrice!, travelerWeight!))
    : 0;
  const notificationFinalEarning = hasCalculation
    ? notificationBasePrice - calculatePlatformFee(notificationBasePrice, (feePercentage || 0) / 100)
    : 0;
  const headlineAmount = isFromNotification ? notificationFinalEarning : finalAmount;

  const buildNotificationFormulaText = (): string => {
    const pricePerLb = calculatePricePerLb(travelerPrice!, travelerWeight!);
    const basePrice = calculateBasePrice(senderWeight!, pricePerLb);
    const platformFee = calculatePlatformFee(basePrice, (feePercentage || 0) / 100);
    const finalEarning = basePrice - platformFee;

    const weight = senderWeight!.toFixed(2);
    const travelerWeightStr = travelerWeight!.toFixed(2);
    const price = travelerPrice!.toFixed(2);
    const pricePerLbStr = pricePerLb.toFixed(2);
    const basePriceStr = basePrice.toFixed(2);
    const feePercentageStr = (feePercentage || 0).toFixed(0);
    const platformFeeStr = platformFee.toFixed(2);
    const finalEarningStr = finalEarning.toFixed(2);

    return `If the package weighs ${weight} lbs then:\nPackage Weight = ${weight} lbs\nTotal Shipping Price = $${price}\nAverage Price Per Pound = $${price}/${travelerWeightStr} = $${pricePerLbStr}/lb\nPlatform Fee = ${feePercentageStr}% of $${basePriceStr} = $${platformFeeStr}\nFinal Earning = Weight × Avg Price Per pound - Platform fee\nFinal Earning = $${basePriceStr} - $${platformFeeStr} = $${finalEarningStr}`;
  };

  const staticRows: Array<{ labelKey: string; valueKey: string }> = [
    { labelKey: 'premiumFirstPoundLabel', valueKey: 'premiumFirstPoundValue' },
    { labelKey: 'remainingWeightLabel', valueKey: 'remainingWeightValue' },
    { labelKey: 'baseEarningLabel', valueKey: 'baseEarningValue' },
    { labelKey: 'serviceFeeLabel', valueKey: 'serviceFeeValue' },
    { labelKey: 'finalPriceLabel', valueKey: 'finalPriceValue' },
  ];
  const staticExampleLines = enTranslations.travelerPricingDialog.staticExampleLines;

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      maxWidth="480px"
      showCloseButton={false}
      customStyles={{
        borderRadius: '12px',
        boxShadow:
          '0px 20px 24px -4px rgba(10, 13, 18, 0.08), 0px 8px 8px -4px rgba(10, 13, 18, 0.03)',
      }}
      innerContentStyle={{ padding: 0 }}
    >
      {/* Sticky header — ripple + icon + title + subtitle */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          background: colors.neutral.white,
          borderRadius: '12px 12px 0 0',
          clipPath: 'inset(0 0 0 0 round 12px 12px 0 0)',
          padding: '24px 24px 20px',
          flexShrink: 0,
        }}
      >
        {/* Corner ripple */}
        <img
          src={cornerRipple}
          alt=""
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '216px',
            height: '216px',
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />

        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            zIndex: 10,
          }}
          aria-label="Close"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="#A4A7AE"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* Icon badge */}
        <div
          style={{
            position: 'relative',
            zIndex: 1,
            width: '48px',
            height: '48px',
            background: colors.traveller.light,
            borderRadius: '9999px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '16px',
          }}
        >
          <img src={offerIcon} alt="" style={{ width: '24px', height: '24px' }} />
        </div>

        {/* Title */}
        <p
          style={{
            position: 'relative',
            zIndex: 1,
            fontFamily: FONT_FAMILY,
            fontWeight: 600,
            fontSize: '20px',
            lineHeight: '30px',
            color: '#181D27',
            margin: '0 0 4px',
          }}
        >
          {hasFeePercentage ? d('title') : d('staticTitle')}
        </p>

        {/* Subtitle */}
        {hasFeePercentage && (
          <p
            style={{
              position: 'relative',
              zIndex: 1,
              fontFamily: FONT_FAMILY,
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '21px',
              color: '#535862',
              margin: 0,
            }}
          >
            {d('subtitle')}
          </p>
        )}
      </div>

      {hasFeePercentage ? (
        /* Body — replicates HowPricingWorks.tsx layout, read-only and pre-populated */
        <div style={{ padding: '0 24px 24px' }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: '16px', width: '100%', marginBottom: '16px' }}>
            <Box sx={{ width: '100%' }}>
              <Typography sx={inputLabelSx}>{t('profile.howPricingWorks.fromLabel')}</Typography>
              <Box sx={readOnlyBoxSx}>
                <Typography component="span" sx={readOnlyTextSx}>{originName}</Typography>
              </Box>
            </Box>
            <Box sx={{ width: '100%' }}>
              <Typography sx={inputLabelSx}>{t('profile.howPricingWorks.toLabel')}</Typography>
              <Box sx={readOnlyBoxSx}>
                <Typography component="span" sx={readOnlyTextSx}>{destinationName}</Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: '16px', width: '100%', marginBottom: '16px' }}>
            <Box sx={{ width: '100%' }}>
              <Typography sx={inputLabelSx}>{t('profile.howPricingWorks.weightLabelSender')}</Typography>
              <Box sx={{ ...readOnlyBoxSx, justifyContent: 'space-between' }}>
                <Typography component="span" sx={readOnlyTextSx}>{hasCalculation ? senderWeight : '--'}</Typography>
                <span style={{ fontFamily: 'Poppins, sans-serif', fontSize: '13px', color: '#777B8B', whiteSpace: 'nowrap', flexShrink: 0 }}>
                  lbs
                </span>
              </Box>
            </Box>
            <Box sx={{ width: '100%' }}>
              <Typography sx={inputLabelSx}>{t('profile.howPricingWorks.priceLabel')}</Typography>
              <Box sx={readOnlyBoxSx}>
                <span style={{ color: '#777B8B', fontSize: '14px', fontFamily: 'Poppins, sans-serif', marginRight: '4px', flexShrink: 0 }}>$</span>
                <Typography component="span" sx={readOnlyTextSx}>{hasCalculation ? travelerPrice!.toFixed(2) : '--'}</Typography>
              </Box>
            </Box>
          </Box>

          {hasCalculation && (
            <Box
              sx={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '8px',
                borderRadius: '12px',
                backgroundColor: 'rgba(255, 245, 250, 0.6)',
                border: '1px solid #D6B0CB',
                boxShadow: '0px 1px 2px rgba(10, 13, 18, 0.05)',
                marginBottom: '16px',
              }}
            >
              <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 600, fontSize: '12px', color: '#000000' }}>
                {t('profile.howPricingWorks.estimatedEarningLabel')}
              </Typography>
              <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 600, fontSize: { xs: '20px', sm: '24px' }, lineHeight: { xs: '30px', sm: '36px' }, color: colors.traveller.primary }}>
                ${headlineAmount.toFixed(2)}
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              width: '100%',
              boxSizing: 'border-box',
              borderRadius: '12px',
              boxShadow: '0px 2px 14px rgba(0, 0, 0, 0.08)',
              backgroundColor: '#FFFFFF',
              marginBottom: '20px',
            }}
          >
            <Box
              onClick={() => setHowItWorksOpen((prev) => !prev)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                padding: '12px',
                cursor: 'pointer',
              }}
            >
              <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 500, fontSize: '16px', lineHeight: '24px', color: '#373940' }}>
                {t('profile.howPricingWorks.howItWorksLabel')}
              </Typography>
              <ChevronDown
                size={20}
                color="#000000"
                style={{ flexShrink: 0, transform: howItWorksOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
              />
            </Box>
            <Collapse in={howItWorksOpen}>
              <Box sx={{ padding: '0 12px 12px' }}>
                <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 400, fontSize: '16px', lineHeight: '24px', color: '#535862', whiteSpace: 'pre-line' }}>
                  {hasCalculation
                    ? (isFromNotification
                        ? buildNotificationFormulaText()
                        : buildPricingFormulaText(senderWeight!, travelerPrice!, travelerWeight!, feePercentage || 0, 'traveler'))
                    : t('profile.howPricingWorks.howItWorksEmptyState')}
                </Typography>
              </Box>
            </Collapse>
          </Box>

          {/* Footer */}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={onClose}
              style={{
                fontFamily: FONT_FAMILY,
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '21px',
                color: colors.neutral.white,
                background: colors.traveller.primary,
                borderRadius: '8px',
                border: 'none',
                padding: '10px 24px',
                cursor: 'pointer',
              }}
            >
              {d('close')}
            </button>
          </div>
        </div>
      ) : (
        /* Static fallback body — plain text table + example, no dynamic calculations */
        <div style={{ padding: '0 24px 24px' }}>
          <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 400, fontSize: '14px', lineHeight: '21px', color: '#535862', marginBottom: '20px' }}>
            {d('staticNotice')}{' '}
            <Box
              component="span"
              onClick={handleSeeHowItWorksNow}
              sx={{
                color: colors.traveller.primary,
                cursor: 'pointer',
                fontWeight: 500,
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {d('staticNoticeLink')}
            </Box>
          </Typography>

          <Box
            sx={{
              width: '100%',
              border: '1px solid #E9EAEB',
              borderRadius: '8px',
              overflow: 'hidden',
              marginBottom: '20px',
            }}
          >
            {staticRows.map((row, index) => (
              <Box
                key={row.labelKey}
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  borderTop: index === 0 ? 'none' : '1px solid #E9EAEB',
                }}
              >
                <Box
                  sx={{
                    width: { xs: '100%', sm: '50%' },
                    padding: '12px 16px',
                    borderRight: { sm: '1px solid #E9EAEB' },
                    borderBottom: { xs: '1px solid #E9EAEB', sm: 'none' },
                  }}
                >
                  <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 600, fontSize: '14px', lineHeight: '21px', color: '#181D27' }}>
                    {d(`staticTableRows.${row.labelKey}`)}
                  </Typography>
                </Box>
                <Box sx={{ width: { xs: '100%', sm: '50%' }, padding: '12px 16px' }}>
                  <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 400, fontSize: '14px', lineHeight: '21px', color: '#535862' }}>
                    {d(`staticTableRows.${row.valueKey}`)}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 600, fontSize: '16px', lineHeight: '24px', color: '#181D27', marginBottom: '8px' }}>
            {d('staticExampleTitle')}
          </Typography>
          <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 400, fontSize: '14px', lineHeight: '21px', color: '#535862', marginBottom: '4px' }}>
            {d('staticExampleIntro')}
          </Typography>
          <Box component="ul" sx={{ margin: '0 0 24px', paddingLeft: '20px' }}>
            {staticExampleLines.map((line) => (
              <Box
                component="li"
                key={line}
                sx={{ fontFamily: FONT_FAMILY, fontWeight: 400, fontSize: '14px', lineHeight: '21px', color: '#535862' }}
              >
                {line}
              </Box>
            ))}
          </Box>

          {/* Footer */}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={onClose}
              style={{
                fontFamily: FONT_FAMILY,
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '21px',
                color: colors.neutral.white,
                background: colors.traveller.primary,
                borderRadius: '8px',
                border: 'none',
                padding: '10px 24px',
                cursor: 'pointer',
              }}
            >
              {d('close')}
            </button>
          </div>
        </div>
      )}
    </BaseDialog>
  );
};
